import { motion } from "motion/react";
import React, { useEffect, useRef, useState } from "react";
import { DYNAMIC_ISLAND } from "../../../../config/dynamicIsland.configuration";
import { type DynamicIslandContentMotionWrapperProps } from "../../dynamicIsland.registry";
import { type DynamicIslandVehicleRealtimePayload } from "../../dynamicIsland.types";
import { hasVehicleRealtimeTextDelta } from "./vehicleRealtime.animation.tools";

/** Brief opacity dip on debounced RT text updates (headsign, delay, stop). */
const VEHICLE_RT_TEXT_PULSE = {
    "duration": 0.22,
    "ease":     "easeInOut",
} as const;

/**
 * Motion wrapper for vehicle realtime island content.
 * Bus-to-bus morph runs inside {@link DynamicIslandVehicleMorphChrome}; this layer
 * only applies a subtle pulse when RT text updates within the same tripId.
 *
 * @param props Wrapper props from the presence shell.
 * @returns Animated children.
 */
const DynamicIslandVehicleRealtimeMotionWrapper: React.FC<
    DynamicIslandContentMotionWrapperProps<DynamicIslandVehicleRealtimePayload>
> = ( { payload, reducedMotion, children } ) => {
    const [ pulseOpacity, setPulseOpacity ] = useState<number>( 1 );
    const previousPayloadRef = useRef<DynamicIslandVehicleRealtimePayload>( payload );
    const pulseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>( null );

    const animationsEnabled = DYNAMIC_ISLAND.animations.enabled && !reducedMotion;

    useEffect( () => {
        const previous = previousPayloadRef.current;

        if (
            animationsEnabled &&
            previous.tripId === payload.tripId &&
            hasVehicleRealtimeTextDelta( previous, payload )
        ) {
            if ( pulseTimeoutRef.current !== null ) {
                clearTimeout( pulseTimeoutRef.current );
            }

            pulseTimeoutRef.current = setTimeout( () => {
                setPulseOpacity( 0.72 );
                pulseTimeoutRef.current = setTimeout( () => {
                    setPulseOpacity( 1 );
                }, 220 );
            }, DYNAMIC_ISLAND.animations.rtTextPulseDebounceMs );
        }

        previousPayloadRef.current = payload;

        return () => {
            if ( pulseTimeoutRef.current !== null ) {
                clearTimeout( pulseTimeoutRef.current );
                pulseTimeoutRef.current = null;
            }
        };
    }, [ animationsEnabled, payload ] );

    if ( !animationsEnabled || pulseOpacity === 1 ) {
        return children;
    }

    return (
        <motion.div
            animate={{ "opacity": pulseOpacity }}
            className="w-full"
            transition={VEHICLE_RT_TEXT_PULSE}
        >
            {children}
        </motion.div>
    );
};

export default DynamicIslandVehicleRealtimeMotionWrapper;
